import { EditorCommittedEdit, EditorDraftOperation } from '../../domain';
import { cloneCanvas } from './canvas-dom.utils';
import {
  applyAnimationFrameBox,
  applyCrop,
  applyResize,
  applyRotate,
} from './canvas-edit-operations';
import { applyFilter } from './canvas-filter-operations';
import { applyWatermark } from './canvas-watermark';

/**
 * Draft kinds that can be previewed by re-rendering the canvas.
 * Crop is an interactive overlay, deskew is backend-only and the
 * animation draft is rendered by the frame player.
 */
export type EditorPreviewableDraft = Extract<
  EditorDraftOperation,
  { kind: 'resize' | 'rotate' | 'filter' | 'watermark' }
>;

export function toPreviewableDraft(
  draft: EditorDraftOperation | null,
): EditorPreviewableDraft | null {
  switch (draft?.kind) {
    case 'resize':
    case 'rotate':
    case 'filter':
    case 'watermark':
      return draft;
    default:
      return null;
  }
}

export function applyCommittedEdits(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  edits: EditorCommittedEdit[],
): HTMLCanvasElement {
  let canvas = sourceCanvas;

  for (const edit of edits) {
    canvas = applyCommittedEdit(document, canvas, edit);
  }

  return canvas;
}

export function applyDraftPreview(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  draft: EditorPreviewableDraft,
): HTMLCanvasElement {
  switch (draft.kind) {
    case 'resize':
      return applyResize(document, sourceCanvas, draft.params);
    case 'rotate':
      return applyRotate(document, sourceCanvas, draft.params);
    case 'filter':
      return applyFilter(document, sourceCanvas, draft.params);
    case 'watermark':
      return applyWatermark(document, sourceCanvas, draft.params);
  }
}

function applyCommittedEdit(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  edit: EditorCommittedEdit,
): HTMLCanvasElement {
  switch (edit.kind) {
    case 'crop':
      return applyCrop(document, sourceCanvas, edit.params);
    case 'resize':
      return applyResize(document, sourceCanvas, edit.params);
    case 'rotate':
      return applyRotate(document, sourceCanvas, edit.params);
    case 'filter':
      return applyFilter(document, sourceCanvas, edit.params);
    case 'watermark':
      return applyWatermark(document, sourceCanvas, edit.params);
    case 'animation':
      return applyAnimationFrameBox(document, sourceCanvas, edit.params);
    case 'deskew':
      // Deskew has no client-side renderer; the backend produces the preview.
      return cloneCanvas(document, sourceCanvas);
  }
}
